import { useMemo, useState } from "react";
import { useAdminUsers } from "@/api/queries";
import { roleLabel } from "@/api/session";
import type { AdminUser } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { CategoryBarChart, StatTile } from "@/components/charts";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input, Select } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { countBy } from "@/lib/analytics";
import { yearsSince } from "@/lib/calendar";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { EMPLOYMENT_TYPE_LABEL, WORK_MODE_LABEL } from "@/lib/labels";

/**
 * The people directory. Every row is a real account; there is no separate
 * "team roster" store, so this is the users table presented as a directory
 * rather than as account administration.
 */
export function HrTeamPage() {
  const users = useAdminUsers();
  const [query, setQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [selected, setSelected] = useState<AdminUser | null>(null);

  // HR & Admin are employees of the company too, so they belong in headcount,
  // analytics and the directory alongside everyone else.
  const staff = users.data ?? [];
  const teams = useMemo(
    () => [...new Set(staff.map((u) => u.team_name ?? "Unassigned"))].sort(),
    [staff],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return staff.filter((person) => {
      const matchesTeam = teamFilter === "all" || (person.team_name ?? "Unassigned") === teamFilter;
      if (!matchesTeam) return false;
      if (!needle) return true;
      return [person.name, person.email, person.designation]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });
  }, [staff, query, teamFilter]);

  const byTeam = useMemo(() => countBy(staff, (u) => u.team_name), [staff]);
  const byDesignation = useMemo(() => countBy(staff, (u) => u.designation).slice(0, 8), [staff]);

  if (users.error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Admin Account only" title="Team Mentoria" />
        <Panel>
          <ErrorState message={users.error.message} onRetry={() => void users.refetch()} />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Admin Account only"
        title="Team Mentoria"
        sub="Everyone with an account on the system, with an overview and a full profile per person."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="People" value={staff.length} icon="team" loading={users.isLoading} />
        <StatTile
          label="Full-time"
          value={staff.filter((u) => u.employment_type === "FTE").length}
          icon="person"
          loading={users.isLoading}
          meta={`${staff.filter((u) => u.employment_type === "Intern").length} interns`}
        />
        <StatTile
          label="Managers"
          value={staff.filter((u) => u.role === "manager").length}
          icon="user-accounts"
          loading={users.isLoading}
        />
        <StatTile label="Teams" value={byTeam.length} icon="org" loading={users.isLoading} />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <CategoryBarChart
          title="People per team"
          data={byTeam}
          valueHeader="People"
          loading={users.isLoading}
          emptyBody="No accounts have been created yet."
        />
        <CategoryBarChart
          title="Most common designations"
          note="Top 8 by headcount"
          data={byDesignation}
          valueHeader="People"
          loading={users.isLoading}
          emptyBody="No accounts have been created yet."
        />
      </div>

      <Panel>
        <PanelHeader
          title="Directory"
          note={`${filtered.length} of ${staff.length} shown`}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Icon
                  name="search"
                  size={16}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-faint"
                />
                <Input
                  aria-label="Search people"
                  placeholder="Search name, email or role"
                  className="h-8 w-56 pl-8 text-[13px]"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <Select
                aria-label="Filter by team"
                className="h-8 w-auto text-[13px]"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
              >
                <option value="all">All teams</option>
                {teams.map((team) => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </Select>
            </div>
          }
        />

        {users.isLoading ? (
          <SkeletonTable rows={8} cols={5} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="team"
            title={staff.length === 0 ? "No accounts yet" : "Nobody matches that"}
            body={
              staff.length === 0
                ? "Create a manager or employee account from the User Accounts screen and they will appear here."
                : "Try a different search or team."
            }
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Designation</TH>
              <TH>Team</TH>
              <TH>Type</TH>
              <TH>Work mode</TH>
              <TH>Joined</TH>
              <TH numeric>Tenure</TH>
            </THead>
            <TBody>
              {filtered.map((person) => (
                <TR key={person.id} className="cursor-pointer">
                  <TDPrimary>
                    <button
                      type="button"
                      onClick={() => setSelected(person)}
                      className="flex items-center gap-2 text-left hover:text-accent"
                    >
                      <Avatar name={person.name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate">{person.name ?? "Unnamed"}</span>
                        <span className="block truncate font-mono text-xs font-normal text-muted">
                          {person.email}
                        </span>
                      </span>
                    </button>
                  </TDPrimary>
                  <TD>{person.designation}</TD>
                  <TD>{person.team_name ?? "Unassigned"}</TD>
                  <TD>
                    <Badge tone="outline">{EMPLOYMENT_TYPE_LABEL[person.employment_type]}</Badge>
                  </TD>
                  <TD>{WORK_MODE_LABEL[person.work_mode]}</TD>
                  <TD className="whitespace-nowrap font-mono text-xs">
                    {formatDate(person.date_joined) ?? "Not set"}
                  </TD>
                  <TD numeric>
                    {person.date_joined ? `${yearsSince(person.date_joined)}y` : "—"}
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      {selected && <PersonCard person={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function PersonCard({ person, onClose }: { person: AdminUser; onClose: () => void }) {
  const rows: { label: string; value: string }[] = [
    { label: "Email", value: person.email },
    { label: "Designation", value: person.designation },
    { label: "Team", value: person.team_name ?? "Unassigned" },
    // roleLabel rather than a manager/employee ternary — that quietly labelled an
    // HR & Admin account as "Employee".
    { label: "Role", value: roleLabel(person) },
    { label: "Employment type", value: EMPLOYMENT_TYPE_LABEL[person.employment_type] },
    { label: "Work mode", value: WORK_MODE_LABEL[person.work_mode] },
    { label: "Date of birth", value: formatDate(person.date_of_birth) ?? "Not entered yet" },
    { label: "Date joined", value: formatDate(person.date_joined) ?? "Not entered yet" },
    { label: "Gender", value: person.gender ?? "Not entered yet" },
    { label: "Account created", value: formatTimestampDate(person.created_at) ?? "—" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close profile"
        className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="relative z-10 max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-xl border border-line bg-surface shadow-pop">
        <div className="flex items-start gap-3 border-b border-line p-5 pr-14">
          <Avatar name={person.name} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-base font-semibold tracking-tight text-ink">
              {person.name ?? "Unnamed"}
            </p>
            <p className="truncate text-sm text-muted">{person.designation}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <dl className="divide-y divide-line">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-4 px-5 py-2.5">
              <dt className="text-[13px] text-muted">{row.label}</dt>
              <dd className="text-right text-[13px] font-medium text-ink">{row.value}</dd>
            </div>
          ))}
        </dl>
        <p className="border-t border-line px-5 py-3 text-xs leading-relaxed text-muted">
          Bank details, addresses and documents live under Documents & Compliance, where access is
          logged separately.
        </p>
      </div>
    </div>
  );
}
